import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LanguageService } from 'services/languageService';
import {
  ISelectedATE,
  ISelectLayer,
  ISelectLayersATE,
  IStatDataset,
  IStatisticsData,
  IToolbarSelect,
  MainLangType,
  MainPageReducer,
} from 'types/mainPage';
import { getCookie } from 'utils/cookie';

const initialState: MainPageReducer = {
  lang: LanguageService.getLanguage(),
  openToolbar: false,
  openSubstrateModal: false,
  isOpenLayerInfo: false,
  isOpenClusterInfo: false,
  isOpenLogin: false,
  isOpenLogout: false,
  isLogged: !!getCookie('user'),
  isLoading: false,
  isError: false,
  user: undefined,
  backdropLoading: false,
  toolbarSelect: [],
  statisticsDatasets: [],
  statisticsAte: [],
  statisticsLayers: [],
  selectLoading: false,
  currentDataset: null,
  currentAteRegions: [],
  currentAteCities: [],
  currentAteVC: [],
  currentLayer: null,
  statisticsData: undefined,
  emptyStatisticsData: false,
  needUpdate: false,
  exportingStats: false,
  exportingExt: '',
  statisticsErrorMsg: '',
  currentTab: 'layers',
};

export const mainPageSlice = createSlice({
  initialState,
  name: 'mainPage',
  reducers: {
    setMainLang(state, action: PayloadAction<MainLangType>) {
      state.lang = action.payload;
    },
    setOpenToolbar(state, action: PayloadAction<boolean>) {
      state.openToolbar = action.payload;
    },
    setOpenSubstrateModal(state, action: PayloadAction<boolean>) {
      state.openSubstrateModal = action.payload;
    },
    toggleOpenLayerInfo(state) {
      state.isOpenLayerInfo = !state.isOpenLayerInfo;
    },
    toggleOpenClusterInfo(state, action: PayloadAction<boolean>) {
      state.isOpenClusterInfo = action.payload;
    },
    setLoginState(state, action: PayloadAction<boolean>) {
      state.isLogged = action.payload;
    },
    setLoginOpen(state, action: PayloadAction<boolean>) {
      state.isOpenLogin = action.payload;
    },
    setLogoutOpen(state, action: PayloadAction<boolean>) {
      state.isOpenLogout = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setBackdropLoading(state, action: PayloadAction<boolean>) {
      state.backdropLoading = action.payload;
    },
    setToolbarSelect(state, action: PayloadAction<IToolbarSelect[]>) {
      state.toolbarSelect = action.payload;
    },
    setSelectLoading(state, action: PayloadAction<boolean>) {
      state.selectLoading = action.payload;
    },
    resetToolbarSelect(state) {
      state.toolbarSelect = [];
    },
    setStatSelect(
      state,
      action: PayloadAction<{
        select: string;
        options:
          | ISelectLayersATE[]
          | IStatDataset[]
          | ISelectLayer[]
          | ISelectedATE[];
      }>
    ) {
      state[action.payload.select as string] = action.payload.options;
    },
    setCurrentStat(
      state,
      action: PayloadAction<{
        param: string;
        value:
          | ISelectLayersATE[]
          | IStatDataset
          | ISelectLayer
          | ISelectedATE[]
          | null
          | undefined;
      }>
    ) {
      state[action.payload.param as string] = action.payload.value;
    },
    setStatisticsData(state, action: PayloadAction<IStatisticsData>) {
      state.statisticsData = action.payload;
      state.emptyStatisticsData = false;
    },
    setEmptyStatisticsData(state, action: PayloadAction<boolean>) {
      state.emptyStatisticsData = action.payload;
    },
    resetStatisticsData(state) {
      state.statisticsData = undefined;
    },
    resetStatSelects(state) {
      state.statisticsLayers = [];
      state.currentDataset = null;
      state.currentAteRegions = [];
      state.currentAteCities = [];
      state.currentAteVC = [];
      state.currentLayer = null;
    },
    setNeedUpdate(state, action: PayloadAction<boolean>) {
      state.needUpdate = action.payload;
    },
    setExporting(
      state,
      action: PayloadAction<{ exporting: boolean; ext: string }>
    ) {
      state.exportingStats = action.payload.exporting;
      state.exportingExt = action.payload.ext;
    },
    setStatisticsErrorMsg(state, action: PayloadAction<string>) {
      state.statisticsErrorMsg = action.payload;
    },
    logInError(state) {
      state.isLoading = false;
      state.isError = true;
    },
    setCurrentsTab(state, action: PayloadAction<string>) {
      state.currentTab = action.payload;
    },
    resetError(state) {
      state.isError = false;
    },
    resetStatisticsDataset(state) {
      state.statisticsDatasets = [];
    },
  },
});

export default mainPageSlice.reducer;
